import { useMemo, type DragEvent } from 'react';
import { FORMATIONS, localizedCode } from '../data/positions.ts';
import { useSquad } from '../lib/squadContext.ts';
import { age, decimal, isForeign, suitability } from '../lib/rules.ts';
import { C, fitBand } from '../lib/theme.ts';
import type { Player, PositionCode, SlotSpec } from '../types.ts';

/** Explanatory copy under the eleven-strong stats row. */
const LINEUP_NOTE =
  'Her oyuncu etiketi (tablo satırı, derinlik çipi, UEFA listeleri, saha kutusu) tıklanınca o oyuncuyu seçer, sürüklenince sahadaki mevkiye yerleşir. Boş kutuya tıklamak seçili oyuncuyu yerleştirir; sağ üstteki ✕ mevkiyi boşaltır. Sürüklerken her mevki, sürüklenen oyuncunun oradaki ağırlığına göre boyanır: yeşil 4–5 (asıl mevkisi), sarı 2–3 (idare eder), kırmızı 1 (acil durum). Kesik kırmızı çerçeve o mevkiyi hiç oynamadığını gösterir; oraya bırakılamaz.';

/** The pitch box: markings, the eleven drop slots and the lineup stats. */
export default function Pitch() {
  const { state, dispatch, players, playerHandles, shortName } = useSquad();

  const byId = useMemo(() => {
    const map = new Map<string, Player>();
    players.forEach((p) => map.set(p.id, p));
    return map;
  }, [players]);

  const dragged = state.draggingId ? (byId.get(state.draggingId) ?? null) : null;

  const slots = useMemo(() => {
    const specs: readonly SlotSpec[] = FORMATIONS[state.formation];
    return specs.map((spec, i) => {
      const code: PositionCode = spec[0];
      const id = state.lineup[i];
      const player = id ? (byId.get(id) ?? null) : null;
      const fit = player ? suitability(player, code) : 0;
      // While a drag is running the whole slot switches to previewing the *dragged*
      // player, band and all. Painting his fit on the border while the fill still shows
      // the current occupant put two different players on one box and read as noise.
      const preview = dragged ? fitBand(suitability(dragged, code)) : null;
      return {
        code,
        player,
        left: spec[1] + '%',
        top: spec[2] + '%',
        name: player ? shortName(player) : '—',
        rating: player ? decimal(player.rating, 2) : '',
        // At rest every occupied box is plain ink. How well each player suits his slot is
        // the depth chart's job now — it says the same thing in the same colours for the
        // whole squad, not just the eleven, so grading the pitch too only added a second
        // red that meant something slightly different.
        bg: preview ? preview.wash : player ? C.ink : C.bg,
        fg: preview ? C.ink : !player ? C.muted : C.bg,
        outOfPosition: !preview && !!player && fit === 0,
        frame: preview ? preview.line : C.ink,
        frameWidth: preview ? '3px' : '2px',
        frameStyle: preview?.dashed ? 'dashed' : 'solid',
        /** The dragged player's weight here, shown so the colour is never a guess. */
        previewWeight: preview ? preview.weight : null,
      };
    });
  }, [state.formation, state.lineup, byId, dragged, shortName]);

  const eleven = useMemo(() => {
    const placed = Object.values(state.lineup)
      .map((id) => byId.get(id))
      .filter((p): p is Player => !!p);
    return {
      power: placed.length
        ? decimal(placed.reduce((total, p) => total + p.rating, 0) / placed.length, 2)
        : '—',
      age: placed.length
        ? decimal(placed.reduce((total, p) => total + age(p), 0) / placed.length, 1)
        : '—',
      foreign: placed.filter(isForeign).length,
    };
  }, [state.lineup, byId]);

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    try {
      event.dataTransfer.dropEffect = 'move';
    } catch {
      // Some browsers refuse to set dropEffect; the drop still works.
    }
  };

  const handleDrop = (slot: number, code: PositionCode) => (event: DragEvent) => {
    event.preventDefault();
    const id = event.dataTransfer.getData('text/plain') || state.draggingId;
    if (!id) return;
    const incoming = byId.get(id);
    // A player who cannot cover the slot is only selected, never placed.
    if (incoming && suitability(incoming, code) === 0) {
      dispatch({ type: 'dragEnd' });
      dispatch({ type: 'selectPlayer', id });
      return;
    }
    // Ending the drag here rather than leaving it to the source element's `onDragEnd`:
    // the drop re-renders the list the chip came from, so the browser's `dragend` can
    // land on a node React has already replaced and the preview never clears.
    dispatch({ type: 'assignSlot', slot, id });
    dispatch({ type: 'dragEnd' });
  };

  const handleClick = (slot: number, code: PositionCode, player: Player | null) => () => {
    if (player) {
      dispatch({ type: 'selectPlayer', id: player.id });
      return;
    }
    const incoming = byId.get(state.selectedId);
    if (!incoming) return;
    if (suitability(incoming, code) === 0) return;
    dispatch({ type: 'assignSlot', slot, id: incoming.id });
  };

  return (
    <div style={{ borderRight: `2px solid ${C.ink}`, padding: 16, background: C.surface }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingTop: '132%',
          border: `2px solid ${C.ink}`,
          background: C.bg,
        }}
      >
        <div style={{ position: 'absolute', inset: 0 }}>
          <svg
            viewBox="0 0 68 90"
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            fill="none"
            stroke={C.line}
            strokeWidth="0.6"
          >
            <line x1="0" y1="45" x2="68" y2="45"></line>
            <ellipse cx="34" cy="45" rx="9.15" ry="7.84"></ellipse>
            <circle cx="34" cy="45" r="0.7" fill={C.line} stroke="none"></circle>
            <rect x="13.84" y="0" width="40.32" height="14.14"></rect>
            <rect x="24.84" y="0" width="18.32" height="4.71"></rect>
            <circle cx="34" cy="9.43" r="0.7" fill={C.line} stroke="none"></circle>
            <path d="M 26.69 14.14 A 9.15 7.84 0 0 0 41.31 14.14"></path>
            <rect x="13.84" y="75.86" width="40.32" height="14.14"></rect>
            <rect x="24.84" y="85.29" width="18.32" height="4.71"></rect>
            <circle cx="34" cy="80.57" r="0.7" fill={C.line} stroke="none"></circle>
            <path d="M 26.69 75.86 A 9.15 7.84 0 0 1 41.31 75.86"></path>
            {/* Corner arcs — 1 m radius, so an ellipse under this box's aspect. Drawn
                thinner than the rest: at this radius the standard stroke would be more
                than half the arc itself and read as a blob. */}
            <path d="M 1 0 A 1 0.857 0 0 1 0 0.857" strokeWidth="0.3"></path>
            <path d="M 67 0 A 1 0.857 0 0 0 68 0.857" strokeWidth="0.3"></path>
            <path d="M 0 89.143 A 1 0.857 0 0 1 1 90" strokeWidth="0.3"></path>
            <path d="M 67 90 A 1 0.857 0 0 1 68 89.143" strokeWidth="0.3"></path>
          </svg>

          {slots.map((slot, i) => (
            <div
              key={i}
              onClick={handleClick(i, slot.code, slot.player)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(i, slot.code)}
              draggable={!!slot.player}
              onDragStart={slot.player ? playerHandles(slot.player.id).onDragStart : undefined}
              onDragEnd={() => dispatch({ type: 'dragEnd' })}
              title="Oyuncu etiketini buraya sürükle"
              style={{
                position: 'absolute',
                left: slot.left,
                top: slot.top,
                transform: 'translate(-50%,-50%)',
                width: 78,
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  border: `${slot.frameWidth} ${slot.frameStyle} ${slot.frame}`,
                  background: slot.bg,
                  color: slot.fg,
                  padding: '5px 3px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    opacity: 0.8,
                  }}
                >
                  {localizedCode(slot.code)}
                  {/* During a drag the band is spelled out, so the colour never has to
                      be decoded from memory. */}
                  {slot.previewWeight !== null && (
                    <span
                      style={{
                        marginLeft: 4,
                        padding: '0 4px',
                        background: slot.frame,
                        color: C.bg,
                      }}
                    >
                      {slot.previewWeight}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {slot.name}
                </div>
                <div style={{ fontSize: 10, fontWeight: 800, marginTop: 1, opacity: 0.85 }}>
                  {slot.rating}
                </div>
                {slot.outOfPosition && (
                  <div
                    style={{
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      marginTop: 2,
                      background: C.gold,
                      color: C.ink,
                    }}
                  >
                    MEVKİ DIŞI
                  </div>
                )}
                {!!slot.player && (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      dispatch({ type: 'clearSlot', slot: i });
                    }}
                    title="Mevkiyi boşalt"
                    style={{
                      position: 'absolute',
                      top: 1,
                      right: 1,
                      width: 15,
                      height: 15,
                      lineHeight: 1,
                      border: `1px solid ${slot.fg}`,
                      background: 'transparent',
                      color: slot.fg,
                      fontSize: 9,
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          marginTop: 14,
          border: `2px solid ${C.ink}`,
        }}
      >
        <div style={{ padding: '9px 11px', borderRight: `1px solid ${C.line}` }}>
          <div
            style={{ fontSize: 9, letterSpacing: '0.12em', color: C.muted, fontWeight: 700 }}
          >
            11 GÜCÜ
          </div>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{eleven.power}</div>
        </div>
        <div style={{ padding: '9px 11px', borderRight: `1px solid ${C.line}` }}>
          <div
            style={{ fontSize: 9, letterSpacing: '0.12em', color: C.muted, fontWeight: 700 }}
          >
            11 YAŞ ORT.
          </div>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{eleven.age}</div>
        </div>
        <div style={{ padding: '9px 11px' }}>
          <div
            style={{ fontSize: 9, letterSpacing: '0.12em', color: C.muted, fontWeight: 700 }}
          >
            11&apos;DE YABANCI
          </div>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{eleven.foreign}</div>
        </div>
      </div>

      <div
        style={{
          fontSize: 11,
          color: C.ink2,
          marginTop: 10,
          lineHeight: 1.5,
          textWrap: 'pretty',
        }}
      >
        {LINEUP_NOTE}
      </div>
    </div>
  );
}
